/**
 * VictoryPodium Entity
 *
 * Phase 5: Victory Zone podium structure with trigger zone
 * Creates a three-tiered stepped platform at the summit
 */

import Phaser from 'phaser';
import { GAME_CONSTANTS } from '../config/constants';

export class VictoryPodium {
  private scene: Phaser.Scene;
  private basePlatform!: Phaser.GameObjects.Graphics;
  private podiumSteps: Phaser.GameObjects.Graphics[] = [];
  private triggerZone!: Phaser.GameObjects.Zone;
  private finishText!: Phaser.GameObjects.Text;
  private sparkleParticles?: Phaser.GameObjects.Particles.ParticleEmitter;
  private x: number;
  private y: number;

  // Store Matter.js bodies separately (NOT as properties on Graphics objects)
  private basePlatformBody!: MatterJS.BodyType;
  private stepBodies: MatterJS.BodyType[] = [];

  constructor(scene: Phaser.Scene, x: number, y: number) {
    this.scene = scene;
    this.x = x;
    this.y = y;

    this.createBasePlatform();
    this.createPodiumSteps();
    this.createTriggerZone();
    this.createFinishText();
    this.createSparkles();
  }

  private createBasePlatform(): void {
    // Create golden base platform (300px wide × 20px tall)
    this.basePlatform = this.scene.add.graphics();
    this.basePlatform.fillStyle(0xffd700, 1.0); // Gold color
    this.basePlatform.fillRect(
      this.x - GAME_CONSTANTS.VICTORY.PODIUM_BASE_WIDTH / 2,
      this.y,
      GAME_CONSTANTS.VICTORY.PODIUM_BASE_WIDTH,
      20
    );

    // Add a subtle shine/highlight
    this.basePlatform.fillStyle(0xffffaa, 0.3);
    this.basePlatform.fillRect(
      this.x - GAME_CONSTANTS.VICTORY.PODIUM_BASE_WIDTH / 2,
      this.y,
      GAME_CONSTANTS.VICTORY.PODIUM_BASE_WIDTH,
      4
    );

    // Add Matter.js physics body for the base platform
    // Store it separately, NOT as a property on the Graphics object
    this.basePlatformBody = this.scene.matter.add.rectangle(
      this.x,
      this.y + 10,
      GAME_CONSTANTS.VICTORY.PODIUM_BASE_WIDTH,
      20,
      { isStatic: true, friction: 0.5, restitution: 0.1 }
    );
  }

  private createPodiumSteps(): void {
    const widths = GAME_CONSTANTS.VICTORY.STEP_WIDTHS;
    const heights = GAME_CONSTANTS.VICTORY.STEP_HEIGHTS;

    // Colors for each step: Bronze → Silver → Gold (bottom to top)
    const colors = [0xcd7f32, 0xc0c0c0, 0xffd700]; // Bronze, Silver, Gold

    let currentY = this.y - 20; // Start above base platform

    for (let i = 0; i < 3; i++) {
      const stepGraphics = this.scene.add.graphics();
      const stepWidth = widths[i] ?? 50;
      const stepHeight = heights[i] ?? 30;
      const stepColor = colors[i] ?? 0xffd700;

      // Draw step
      stepGraphics.fillStyle(stepColor, 1.0);
      stepGraphics.fillRect(this.x - stepWidth / 2, currentY, stepWidth, stepHeight);

      // Add highlight for 3D effect
      stepGraphics.fillStyle(0xffffff, 0.2);
      stepGraphics.fillRect(this.x - stepWidth / 2, currentY, stepWidth, 3);

      // Add Matter.js physics body for each step
      // Store it separately, NOT as a property on the Graphics object
      const stepBody = this.scene.matter.add.rectangle(
        this.x,
        currentY + stepHeight / 2,
        stepWidth,
        stepHeight,
        { isStatic: true, friction: 0.5, restitution: 0.1 }
      );
      this.stepBodies.push(stepBody);

      this.podiumSteps.push(stepGraphics);
      currentY -= stepHeight;
    }
  }

  private createTriggerZone(): void {
    // Create invisible trigger zone at the top of the podium
    const topStepY = this.y - 20 - 90; // Base - steps height

    this.triggerZone = this.scene.add.zone(
      this.x,
      topStepY - GAME_CONSTANTS.VICTORY.TRIGGER_HEIGHT / 2,
      GAME_CONSTANTS.VICTORY.STEP_WIDTHS[2], // Width of top step (50px)
      GAME_CONSTANTS.VICTORY.TRIGGER_HEIGHT
    );

    // For debugging (can be disabled in production)
    if (GAME_CONSTANTS.DEBUG.SHOW_PHYSICS) {
      const debugGraphics = this.scene.add.graphics();
      debugGraphics.lineStyle(2, 0x00ff00, 0.5);
      debugGraphics.strokeRect(
        this.triggerZone.x - this.triggerZone.width / 2,
        this.triggerZone.y - this.triggerZone.height / 2,
        this.triggerZone.width,
        this.triggerZone.height
      );
    }
  }

  private createFinishText(): void {
    // Create "FINISH" text above the podium
    const textY = this.y - 20 - 90 - 50; // Above the top step

    this.finishText = this.scene.add.text(this.x, textY, '🏁 FINISH 🏁', {
      fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
      fontSize: '32px',
      color: GAME_CONSTANTS.UI.COLOR_ACCENT,
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4,
    });
    this.finishText.setOrigin(0.5);

    // Add pulsing animation
    this.scene.tweens.add({
      targets: this.finishText,
      scale: { from: 1.0, to: 1.2 },
      duration: 1000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private createSparkles(): void {
    // Create sparkle particle effect around the podium
    // Simple implementation without texture for compatibility
    const sparkleGraphics = this.scene.add.graphics();
    sparkleGraphics.fillStyle(0xffffff, 1);
    sparkleGraphics.fillCircle(2, 2, 2);
    sparkleGraphics.generateTexture('sparkle', 4, 4);
    sparkleGraphics.destroy();

    this.sparkleParticles = this.scene.add.particles(this.x, this.y - 50, 'sparkle', {
      speed: { min: 20, max: 50 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.5, end: 0 },
      alpha: { start: 1, end: 0 },
      lifespan: 1500,
      frequency: 100,
      quantity: 2,
      blendMode: 'ADD',
    });
  }

  public getTriggerZone(): Phaser.GameObjects.Zone {
    return this.triggerZone;
  }

  public playIdleAnimation(): void {
    // Gentle sparkle/glow animation - already handled by particle emitter
  }

  public destroy(): void {
    // Remove Matter.js bodies from world using separate properties
    // Check if Matter world still exists before trying to remove bodies
    if (this.scene.matter?.world) {
      if (this.basePlatformBody) {
        this.scene.matter.world.remove(this.basePlatformBody);
      }

      this.stepBodies.forEach(body => {
        this.scene.matter.world.remove(body);
      });
    }

    // Now destroy the graphics objects (safe - no body properties attached)
    this.basePlatform?.destroy();
    this.podiumSteps.forEach(step => step.destroy());
    this.triggerZone?.destroy();
    this.finishText?.destroy();
    this.sparkleParticles?.destroy();
  }
}
